import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { NewShareInfo, Share, User as MojoUser } from 'mojo-share-resource';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, startWith, take } from 'rxjs/operators';

import { getRouterQueryParams } from '@app/app.reducer';
import { JobsCreate, JobsUpdate } from '@collections/jobs';
import { getUsersLoggedIn } from '@collections/users';
import { convertTeamMemberListItemsToTeamMembers } from '@modules/jobs-page/utils/utils';
import { GeolocationService, Job, JobTypes, Location, TimePeriods, User } from '@shared';
import { DangerAlert } from '@shared/alert';
import { TeamMembersListItem } from '@shared/models/team-members-list-item.model';
import { TeamMembers } from '@shared/models/team-members.model';
import { TEAM_ROLES, TeamRole } from '@shared/models/team-role.enum';
import { isLocation } from '@shared/validators/is-location.validator';
import { isObject } from '@shared/validators/is-object.validator';

@Component({
  selector: 'mojo-jobs-form-page',
  templateUrl: './job-form.container.html',
  styleUrls: ['./job-form.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('stepper', { static: false, read: ElementRef }) stepper: ElementRef;
  accessLevelOptions = TEAM_ROLES.map((role) => ({
    id: role,
    displayName: role,
    disabled: false,
  }));
  teamRolesSortOrder = TEAM_ROLES;

  @Input()
  editMode: boolean;

  @Input()
  job: Job;

  @Input()
  isClonedJob = false;

  user$: Observable<User> = this.store.select(getUsersLoggedIn);
  submitted = false;
  textAndNumberPattern = '^[a-zA-Z0-9_.-]*$';
  textAndNumberValidator = Validators.pattern(this.textAndNumberPattern);
  subscriptions: Subscription = new Subscription();
  status = new UntypedFormControl(null, Validators.required);

  id: UntypedFormControl = new UntypedFormControl();
  date: UntypedFormControl = new UntypedFormControl();
  durationYears: UntypedFormControl = new UntypedFormControl();
  durationMonths: UntypedFormControl = new UntypedFormControl();
  locationInput: UntypedFormControl = new UntypedFormControl(
    null,
    [Validators.maxLength(500)],
    [isLocation(this.geolocationService)],
  );
  private companyManager: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    isObject(),
  ]);
  private company: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    isObject(),
  ]);

  basicForm: UntypedFormGroup = new UntypedFormGroup({
    locationInput: this.locationInput,
    id: this.id,
    date: this.date,
    durationYears: this.durationYears,
    durationMonths: this.durationMonths,
    title: new UntypedFormControl(null, Validators.required),
    locations: new UntypedFormControl(),
    jobType: new UntypedFormControl(null, Validators.required),
    targetDate: new UntypedFormControl(),
    workExperience: new UntypedFormControl(),
    numberOfPositions: new UntypedFormControl(1),
    minSalary: new UntypedFormControl(null, this.numericDotValidator),
    maxSalary: new UntypedFormControl(null, this.numericDotValidator),
    budgetPer: new UntypedFormControl(),
    remote: new UntypedFormControl(false),
    bonus: new UntypedFormControl(null, this.textAndNumberValidator),
    bonusPer: new UntypedFormControl(),
    salaryPeriod: new UntypedFormControl(TimePeriods.Annually),
    bonusPeriod: new UntypedFormControl(TimePeriods.Annually),
    salaryCurrency: new UntypedFormControl('USD'),
    bonusCurrency: new UntypedFormControl('USD'),
    payRateCurrency: new UntypedFormControl('USD'),
    billRateCurrency: new UntypedFormControl('USD'),
    commission: new UntypedFormControl(),
    minPayRate: new UntypedFormControl(null, this.numericDotValidator),
    maxPayRate: new UntypedFormControl(null, this.numericDotValidator),
    payRatePeriod: new UntypedFormControl(TimePeriods.Annually),
    minBillRate: new UntypedFormControl(null, this.numericDotValidator),
    maxBillRate: new UntypedFormControl(null, this.numericDotValidator),
    billRatePeriod: new UntypedFormControl(TimePeriods.Annually),
    companyManager: this.companyManager,
    company: this.company,
    clientReqId: new UntypedFormControl(),
  });
  descriptionForm: UntypedFormGroup = new UntypedFormGroup({
    companyDescription: new UntypedFormControl(),
    description: new UntypedFormControl(null, Validators.required),
    highLevelRequirements: new UntypedFormControl(null, Validators.required),
    qualification: new UntypedFormControl(),
    responsibilities: new UntypedFormControl(),
    education: new UntypedFormControl(),
    additionalInformation: new UntypedFormControl(),
  });
  statusForm: UntypedFormGroup = new UntypedFormGroup({
    status: new UntypedFormControl(null, Validators.required),
  });
  shares: Share[] = [];
  teamMembers: TeamMembers;
  editSteps: UntypedFormGroup[] = [this.basicForm, this.descriptionForm];
  creationSteps: UntypedFormGroup[] = [...this.editSteps, this.statusForm];

  initialStepIndex = 0;
  loading = false;
  disabled = true;
  isRequired = true;

  companyChanges$ = this.company.valueChanges.pipe(
    startWith(this.basicForm.get('company').value),
    distinctUntilChanged(),
  );

  @Output() save = new EventEmitter();

  constructor(
    private store: Store<{}>,
    private geolocationService: GeolocationService,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    this.wrapStepperHeaderInScrollableDiv();
  }

  numericDotValidator(control: UntypedFormControl) {
    const regex = /^\d*(\.\d+)?$/;
    if (control.value && !regex.test(control.value)) {
      return { invalidFormat: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.basicForm.valueChanges.subscribe((value) => {
      this.disabled = this.validateProperties(value);
      this.validateRequired(value);
    });

    this.subscriptions.add(
      this.store
        .select(getRouterQueryParams)
        .pipe(filter((qp) => qp && qp.selectedStep))
        .subscribe((qp) => (this.initialStepIndex = qp.selectedStep)),
    );

    this.subscriptions.add(
      this.companyChanges$.subscribe((companyChanges) => {
        if (!!companyChanges) {
          this.companyManager.enable();
        } else if (typeof companyChanges === 'string' || companyChanges === null) {
          this.companyManager.disable();
        }
      }),
    );

    if (!this.isClonedJob) {
      this.subscriptions.add(
        this.user$
          .pipe(
            filter((user) => !!user),
            take(1),
          )
          .subscribe((user) => {
            this.shares = [
              {
                user: user as MojoUser,
                id: user.id,
                accessLevel: TeamRole.regularRecruiter,
              },
            ];
          }),
      );
    }
  }
  validateRequired(value: any) {
    const selectedLocation: Location[] = value.locationInput;

    if (value.remote === true) this.isRequired = false;
    else if (selectedLocation === null || !selectedLocation.length) this.isRequired = true;
    else this.isRequired = false;
  }

  validate(form: UntypedFormGroup): boolean {
    if (form.invalid) {
      form.markAllAsTouched();
      this.store.dispatch(
        new DangerAlert('Correct the errors below before moving to the next step.'),
      );
    }
    return form.valid;
  }

  /**
   * Iterate over forms from selected flow (creation/edit) and check if all are valid
   */
  checkValidity(creationSteps: UntypedFormGroup[], editSteps: UntypedFormGroup[]): boolean[] {
    if (this.editMode) {
      return editSteps.map((formToCheck) => this.validate(formToCheck));
    } else {
      return creationSteps.map((formToCheck) => this.validate(formToCheck));
    }
  }

  submit() {
    if (this.submitted) {
      return;
    }

    if (!this.id.value && this.isClonedJob) {
      this.submitted = true;
    }

    this.subscriptions.add(
      this.user$.subscribe((user) => {
        if (!this.checkValidity(this.creationSteps, this.editSteps)) {
          this.submitted = false;
          return;
        }

        if (!!this.locationInput.value) {
          const selectedLocation: Location[] = this.locationInput.value;
          const isSow = this.basicForm.get('jobType').value === JobTypes.SOW;
          if (isSow) {
            this.removeOldValuesFromNewSOWJobs();
          }
          this.basicForm.setValue({
            ...this.basicForm.getRawValue(), // getRawValue includes disabled fields
            locations: selectedLocation.map((location) => ({
              city: location.name,
              state: location.stateCode,
              country: location.countryCode,
            })),
          });
        }

        const targetDate: string = !!this.date.value
          ? `${format(new Date(this.date.value), 'yyyy-MM-dd')}T12:00`
          : null;
        const job: Job = this.generateJob(targetDate);
        this.save.emit();
        this.saveJobOnStore(job, user);
      }),
    );
  }

  private removeOldValuesFromNewSOWJobs() {
    this.basicForm.get('minBillRate').setValue(null);
    this.basicForm.get('maxBillRate').setValue(null);
    this.basicForm.get('minPayRate').setValue(null);
    this.basicForm.get('maxPayRate').setValue(null);
  }

  generateJob(targetDate): Job {
    // don't miss any JobDescription field as it will use PUT request
    // and everything needs to be present in request
    return {
      ...this.job,
      ...this.basicForm.getRawValue(),
      ...this.descriptionForm.getRawValue(),
      ...(this.editMode ? { status: this.job.status } : this.statusForm.value),
      hiringManagers: this.teamMembers?.hiringManagers,
      recruitingManagers: this.teamMembers?.recruitingManagers,
      regularRecruiters: this.teamMembers?.regularRecruiters,
      coordinators: this.teamMembers?.coordinators,
      companyId: this.company.value.id,
      companyName: this.company.value.name,
      companyManagerId: this.companyManager.value.id,
      targetDate,
      duration: this.durationYears.value * 12 + this.durationMonths.value,
      id: this.id.value && !this.isClonedJob ? this.id.value : null,
      createdDate: this.isClonedJob ? new Date().getTime() : this.job?.createdDate,
    };
  }

  saveJobOnStore(job: Job, user) {
    // TODO: move decision to effect?
    this.store.dispatch(
      this.id.value && !this.isClonedJob
        ? new JobsUpdate(job)
        : new JobsCreate({
            ...job,
            recruiters: [{ name: `${user.firstName} ${user.lastName}` }],
          }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onAddShare(newShareInfo: NewShareInfo) {
    const newShares: Share[] = newShareInfo.usersToInvite.map((user) => ({
      user,
      accessLevel: newShareInfo.accessLevel,
      id: user.id,
    }));
    this.shares = [...this.shares, ...newShares];
    this.updateTeamMembers();
  }

  onShareDelete(userId: number) {
    this.shares = this.shares.filter((share) => !!share.user && share.user.id !== userId);
    this.updateTeamMembers();
  }

  onUserAccessChange(access) {
    const shareIndex = this.shares.findIndex(
      (share) => !!share.user && share.user.id === access.user.id,
    );
    const updatedShare = {
      ...this.shares[shareIndex],
      accessLevel: access.accessLevel,
    };
    const shares = [...this.shares];
    shares.splice(shareIndex, 1, updatedShare);
    this.shares = shares;
    this.updateTeamMembers();
  }

  private wrapStepperHeaderInScrollableDiv() {
    // Workaround for issue with mat-stepper header not being scrollable: https://github.com/angular/components/issues/15181
    const stepperHeader = this.stepper.nativeElement.querySelector(
      '.mat-horizontal-stepper-header-container',
    );
    const headerWrapper = this.renderer.createElement('div');
    this.renderer.addClass(headerWrapper, 'job-horizontal-stepper-header-wrapper');
    this.renderer.removeChild(this.stepper.nativeElement, stepperHeader);
    this.renderer.appendChild(headerWrapper, stepperHeader);
    this.renderer.insertBefore(
      this.stepper.nativeElement,
      headerWrapper,
      this.stepper.nativeElement.firstChild,
    );
  }

  private updateTeamMembers() {
    const teamMemberListItems: TeamMembersListItem[] = this.shares.map((share) => ({
      user: share.user as User,
      role: share.accessLevel as TeamRole,
    }));
    this.teamMembers = convertTeamMemberListItemsToTeamMembers(teamMemberListItems);
  }

  validateProperties(form: any): boolean {
    return ((form.locationInput !== null ? form.locationInput.length === 0 : true) &&
      (form.remote === false || form.remote === null)) ||
      this.basicForm.get('title').invalid ||
      this.basicForm.get('company').invalid ||
      this.basicForm.get('companyManager').invalid ||
      this.basicForm.get('jobType').invalid
      ? true
      : false;
  }
}
